import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageCircle, Copy, ExternalLink } from "lucide-react";
import { buildWhatsAppUrl, defaultWhatsAppMessage, waPhoneDigits } from "@/lib/contactExport";
import { useAuth } from "@/context/AuthContext";

const STORE_KEY = "taru.wa_outreach_defaults";

/**
 * Composer modal that opens over the scan-review panel. Pre-fills the outreach
 * template with {user.name} + {company.name} + {company.city}; user can edit the
 * text and the "meeting event" line before firing WhatsApp.
 */
export function WhatsAppShareDialog({ open, onOpenChange, parsed }) {
  const { user, company } = useAuth();
  const stored = (() => { try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch { return {}; } })();
  const [event, setEvent] = useState(stored.event ?? "IIJS exhibition 2026");
  const [website, setWebsite] = useState(stored.website ?? "https://tarujewels.com/");
  const [senderCity, setSenderCity] = useState(stored.senderCity ?? "Mumbai");
  const [msg, setMsg] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (!open) return;
    setPhone(parsed?.phones?.[0] || "");
    setMsg(defaultWhatsAppMessage({
      contactName: (parsed?.name || "").split(/\s+/)[0],
      senderName: user?.name,
      companyName: company?.name,
      companyCity: senderCity,
      website,
      event,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, parsed?.phones, parsed?.name, user?.name, company?.name]);

  // Keep msg in sync when user tweaks event/website/city.
  useEffect(() => {
    if (!open) return;
    setMsg(defaultWhatsAppMessage({
      contactName: (parsed?.name || "").split(/\s+/)[0],
      senderName: user?.name,
      companyName: company?.name,
      companyCity: senderCity,
      website,
      event,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, website, senderCity]);

  const persistDefaults = () => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ event, website, senderCity })); } catch { /* ignore quota */ }
  };

  const sendWhatsApp = () => {
    persistDefaults();
    const url = buildWhatsAppUrl(phone, msg);
    // window.open with a user gesture allows the WhatsApp app to intercept on mobile.
    window.open(url, "_blank");
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(msg);
      toast.success("Message copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const digits = waPhoneDigits(phone);
  const missingPhone = !digits;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="wa-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <MessageCircle size={18} className="text-[#25D366]"/> Send via WhatsApp
          </DialogTitle>
          <DialogDescription className="text-xs">
            Opens WhatsApp (or WhatsApp Business) with the message pre-filled. Edit anything before you send.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Phone (auto-normalised to +91)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. +91 98765 43210"
              data-testid="wa-phone-input"/>
            {missingPhone && (
              <div className="text-[11px] text-[#4A5A50] mt-1">No phone yet — WhatsApp will ask you to pick a contact.</div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Meeting event</Label>
              <Input value={event} onChange={(e) => setEvent(e.target.value)}
                placeholder="IIJS exhibition 2026"
                data-testid="wa-event-input"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Your city</Label>
              <Input value={senderCity} onChange={(e) => setSenderCity(e.target.value)}
                placeholder="Mumbai"
                data-testid="wa-city-input"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Website</Label>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://tarujewels.com/"
                data-testid="wa-website-input"/>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest">Message</Label>
            <Textarea rows={7} value={msg} onChange={(e) => setMsg(e.target.value)}
              className="font-mono text-xs"
              data-testid="wa-message-input"/>
            <div className="text-[10px] text-[#4A5A50] mt-1">
              {msg.length} chars · Signed in as {user?.name} · {company?.name}{senderCity ? " · " + senderCity : ""}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Button variant="outline" size="sm" className="rounded-full" onClick={copyMessage} data-testid="wa-copy-btn">
              <Copy size={12} className="mr-1"/> Copy
            </Button>
            <Button
              size="sm"
              className="rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white"
              onClick={sendWhatsApp}
              data-testid="wa-send-btn"
            >
              <ExternalLink size={12} className="mr-1"/> Open WhatsApp
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
